/**
 * ResumeCheckoutBanner — abandoned-payment recovery (v35.43).
 *
 * If the signed-in user has a still-open Stripe checkout session (created in
 * the last 30 min, not yet paid), show a slim banner offering to resume it.
 * Hidden when: not signed in, no pending session, returning from Stripe
 * (?session_id= present), or dismissed this browser session.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowRight, X, ShoppingCart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ResumeCheckoutBanner = () => {
  const [pending, setPending] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_id')) return; // already returning from Stripe
    if (sessionStorage.getItem('resume_checkout_dismissed')) return;
    axios
      .get(`${API}/payments/pending-checkout`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => { if (r.data?.pending) setPending(r.data); })
      .catch(() => {});
  }, []);

  if (!pending || dismissed) return null;

  const mins = Math.max(1, Math.round(pending.expires_in_seconds / 60));
  const amount = typeof pending.amount === 'number'
    ? `$${pending.amount.toFixed(2)}` : '';

  return (
    <div
      className="max-w-3xl mx-auto mb-8 rounded-xl bg-emerald-500/10 ring-1 ring-emerald-500/40 px-5 py-3.5 flex items-center gap-3"
      data-testid="resume-checkout-banner"
    >
      <ShoppingCart className="w-4 h-4 text-emerald-300 shrink-0" />
      <p className="flex-1 text-sm text-slate-200">
        You have an unfinished checkout for{' '}
        <span className="font-semibold text-emerald-300">{pending.plan_name}</span>
        {amount && <> ({amount})</>} — held for another ~{mins} min.
      </p>
      <a
        href={pending.url}
        className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-950 bg-emerald-500 hover:bg-emerald-400 rounded-lg px-3.5 py-1.5 whitespace-nowrap transition-colors"
        data-testid="resume-checkout-link"
      >
        Resume checkout <ArrowRight className="w-3.5 h-3.5" />
      </a>
      <button
        onClick={() => {
          sessionStorage.setItem('resume_checkout_dismissed', '1');
          setDismissed(true);
        }}
        className="text-slate-400 hover:text-slate-200 transition-colors"
        aria-label="Dismiss"
        data-testid="resume-checkout-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
